import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { interFreqX1HoCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the InterFreqX1HoCell component
 */
interface InterFreqX1HoCellProps {
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
  /** Current fullscreen state */
  isFullscreen: boolean;
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
}

/**
 * Inter-Frequency X1 Handover Chart (LTE Cell Level)
 * 
 * Displays inter-frequency handover metrics over the X1 interface for individual LTE cells.
 * Shows handovers between cells operating on different frequencies within the same eNB or between eNBs.
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level inter-frequency X1 handover metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <InterFreqX1HoCell
 *   cellName="173716_145"
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (interFreqX1HoCellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/inter-freq-x1-ho?cell_name={cellName}
 */

const InterFreqX1HoCell: React.FC<InterFreqX1HoCellProps> = ({
  cellName,
  isFullscreen,
  onFullscreenToggle
}) => {
  const additionalParams = useMemo(
    () => ({
      cell_name: cellName
    }),
    [cellName]
  );

  return (
    <SiteCharts
      config={interFreqX1HoCellChartConfig}
      additionalParams={additionalParams}
      isFullscreen={isFullscreen}
      onFullscreenToggle={onFullscreenToggle}
    />
  );
};

export default InterFreqX1HoCell;
